// icons.evvect.com frontend. Plain TypeScript, no framework: one JSON fetch, a virtualised grid,
// a detail drawer and a docs view. Bundled by scripts/build-web.ts.

type Row = [name: string, exportName: string, aliases: string, codepoint: number, body: string];
interface IconsJson { version: string; count: number; icons: Row[] }
interface Pkg { version: string; tarball: string; license: string }

const ORIGIN = location.origin.includes("localhost") || location.origin.includes("127.0.0.1")
  ? location.origin
  : "https://icons.evvect.com";

const $ = <T extends HTMLElement = HTMLElement>(sel: string) => document.querySelector(sel) as T;
const fmt = new Intl.NumberFormat("en-GB");

const state = {
  icons: [] as Row[],
  byName: new Map<string, number>(),
  hay: [] as { name: string; compact: string; words: string[]; aliases: string }[],
  results: [] as number[],
  version: "",
  packages: {} as Record<string, Pkg>,
  size: 24,
  stroke: 1.5,
  color: "" as string, // "" = currentColor
  selected: -1,
  tab: "font",
  lastFocus: null as HTMLElement | null,
};

// ---------- helpers ----------

function esc(s: string) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function num(v: number) {
  return String(Math.round(v * 1000) / 1000);
}

function bodyFor(i: number, stroke = state.stroke, color = state.color) {
  let body = state.icons[i]![4];
  if (stroke !== 1.5) {
    const f = stroke / 1.5;
    body = body.replace(/stroke-width="([\d.]+)"/g, (_, w) => `stroke-width="${num(parseFloat(w) * f)}"`);
  }
  if (color) body = body.replaceAll('"currentColor"', `"${color}"`);
  return body;
}

function svgFor(i: number, size = state.size, stroke = state.stroke, color = state.color) {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none">${bodyFor(i, stroke, color)}</svg>`;
}

function uiIcon(name: string) {
  const i = state.byName.get(name);
  return i === undefined ? "" : `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">${state.icons[i]![4]}</svg>`;
}

function paintUiIcons(root: ParentNode = document) {
  root.querySelectorAll<HTMLElement>("[data-icon]").forEach((el) => {
    if (el.dataset.painted === el.dataset.icon) return;
    el.innerHTML = uiIcon(el.dataset.icon!);
    el.dataset.painted = el.dataset.icon;
  });
}

let toastTimer = 0;
function toast(msg: string) {
  const t = $("#toast");
  t.innerHTML = `<span class="hgi-inline">${uiIcon("tick-02")}</span>${esc(msg)}`;
  t.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => (t.hidden = true), 1800);
}

async function copy(text: string, what = "Copied") {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.append(ta);
    ta.select();
    document.execCommand("copy");
    ta.remove();
  }
  toast(what);
}

function store(key: string, value?: string) {
  try {
    if (value === undefined) return localStorage.getItem(key);
    if (value === "") localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {}
  return null;
}

// ---------- search ----------

function search(q: string): number[] {
  const query = q.trim().toLowerCase();
  const terms = query.split(/[\s_-]+/).filter(Boolean);
  if (!terms.length) return state.icons.map((_, i) => i);
  const exact = query.replace(/\s+/g, "-");
  const scored: [number, number][] = [];
  state.hay.forEach((h, i) => {
    let score = 0;
    for (const t of terms) {
      const s =
        h.name === t || h.compact === t ? 100
        : h.words.includes(t) ? 40
        : h.name.startsWith(t) ? 30
        : h.name.includes(t) || h.compact.includes(t) ? 15
        : h.aliases.includes(t) ? 8
        : 0;
      if (!s) return;
      score += s;
    }
    if (h.name === exact) score += 200;
    scored.push([score * 100 - h.name.length, i]);
  });
  scored.sort((a, b) => b[0] - a[0] || state.hay[a[1]]!.name.localeCompare(state.hay[b[1]]!.name));
  return scored.map((s) => s[1]);
}

// ---------- virtual grid ----------

const grid = {
  el: null as unknown as HTMLElement,
  cols: 8,
  tileH: 112,
  gap: 8,
  pad: 12,
  first: -1,
  last: -1,
};

function layout() {
  const width = grid.el.clientWidth - grid.pad * 2;
  const phone = window.matchMedia("(max-width: 720px)").matches;
  grid.tileH = phone ? 88 : 96;
  const min = phone ? 92 : 120;
  grid.cols = Math.max(3, Math.floor((width + grid.gap) / (min + grid.gap)));
  const rows = Math.ceil(state.results.length / grid.cols);
  grid.el.style.height = rows ? `${rows * (grid.tileH + grid.gap) - grid.gap + grid.pad * 2}px` : "0px";
  grid.first = grid.last = -1;
  renderVisible();
}

function renderVisible(force = false) {
  const rowH = grid.tileH + grid.gap;
  const rect = grid.el.getBoundingClientRect();
  const top = -rect.top - grid.pad;
  const rows = Math.ceil(state.results.length / grid.cols);
  const first = Math.max(0, Math.floor(top / rowH) - 3);
  const last = Math.min(rows - 1, Math.ceil((top + window.innerHeight) / rowH) + 3);
  if (!force && first === grid.first && last === grid.last) return;
  grid.first = first;
  grid.last = last;
  const html: string[] = [];
  const style = `grid-template-columns:repeat(${grid.cols},minmax(0,1fr))`;
  for (let r = first; r <= last; r++) {
    html.push(`<div class="row" style="top:${grid.pad + r * rowH}px;${style}">`);
    for (let c = 0; c < grid.cols; c++) {
      const k = r * grid.cols + c;
      if (k >= state.results.length) break;
      const i = state.results[k]!;
      const name = state.icons[i]![0];
      html.push(
        `<button class="tile" role="listitem" data-i="${i}" title="${name}"${i === state.selected ? ' aria-current="true"' : ""}>` +
          `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">${bodyFor(i)}</svg>` +
          `<span class="tile-name">${name}</span></button>`,
      );
    }
    html.push("</div>");
  }
  grid.el.innerHTML = html.join("");
}

let searchTimer = 0;
function applySearch(q: string, push = true) {
  state.results = search(q);
  const n = state.results.length;
  $("#count").textContent = fmt.format(n);
  $("#count-label").textContent = n === 1 ? "icon" : "icons";
  $("#grid-title").textContent = q.trim() ? `Results for “${q.trim()}”` : "All icons";
  $("#empty").hidden = n > 0;
  $("#empty-q").textContent = q.trim();
  layout();
  if (push) {
    const url = new URL(location.href);
    if (q.trim()) url.searchParams.set("q", q.trim());
    else url.searchParams.delete("q");
    history.replaceState(history.state, "", url);
  }
}

// ---------- detail drawer ----------

const TABS: [id: string, label: string][] = [
  ["font", "Font"],
  ["svg", "SVG"],
  ["react", "React"],
  ["vue", "Vue"],
  ["svelte", "Svelte"],
  ["angular", "Angular"],
  ["rn", "React Native"],
  ["wc", "Web component"],
  ["url", "URL"],
  ["esm", "ES module"],
];

function pkgInstall(...names: string[]) {
  const urls = names.map((n) => state.packages[n]).filter(Boolean).map((p) => `${ORIGIN}/npm/${p!.tarball}`);
  return `bun add ${urls.join(" ")}`;
}

function snippet(i: number, tab: string): { code: string; note: string } {
  const [name, exp] = state.icons[i]!;
  const size = state.size;
  const sw = num(state.stroke);
  const color = state.color || "currentColor";
  const v = state.packages["@hugeicons/core-free-icons"]?.version ?? state.version;
  const q = new URLSearchParams();
  if (size !== 24) q.set("size", String(size));
  if (state.color) q.set("color", state.color.replace("#", ""));
  if (state.stroke !== 1.5) q.set("stroke", sw);
  const qs = q.toString() ? `?${q}` : "";
  switch (tab) {
    case "font":
      return {
        code: `<link rel="stylesheet" href="${ORIGIN}/font/hgi-stroke-rounded.css" />\n\n<i class="hgi hgi-stroke hgi-${name}"></i>`,
        note: "Same class names as the Hugeicons CDN: only the stylesheet URL changes. Size with font-size, colour with color. The font has one stroke width (1.5).",
      };
    case "svg":
      return { code: svgFor(i), note: "Uses currentColor unless you picked a colour, so it follows the text colour around it." };
    case "react":
      return {
        code: `import { HugeiconsIcon } from "@hugeicons/react";\nimport { ${exp} } from "@hugeicons/core-free-icons";\n\n<HugeiconsIcon icon={${exp}} size={${size}} color="${color}" strokeWidth={${sw}} />`,
        note: `Install from this mirror: ${pkgInstall("@hugeicons/core-free-icons", "@hugeicons/react")}`,
      };
    case "vue":
      return {
        code: `<script setup>\nimport { HugeiconsIcon } from "@hugeicons/vue";\nimport { ${exp} } from "@hugeicons/core-free-icons";\n</script>\n\n<template>\n  <HugeiconsIcon :icon="${exp}" :size="${size}" color="${color}" :stroke-width="${sw}" />\n</template>`,
        note: `Install from this mirror: ${pkgInstall("@hugeicons/core-free-icons", "@hugeicons/vue")}`,
      };
    case "svelte":
      return {
        code: `<script>\n  import { HugeiconsIcon } from "@hugeicons/svelte";\n  import { ${exp} } from "@hugeicons/core-free-icons";\n</script>\n\n<HugeiconsIcon icon={${exp}} size={${size}} color="${color}" strokeWidth={${sw}} />`,
        note: `Install from this mirror: ${pkgInstall("@hugeicons/core-free-icons", "@hugeicons/svelte")}`,
      };
    case "angular":
      return {
        code: `import { Component } from "@angular/core";\nimport { HugeiconsIconComponent } from "@hugeicons/angular";\nimport { ${exp} } from "@hugeicons/core-free-icons";\n\n@Component({\n  selector: "app-example",\n  imports: [HugeiconsIconComponent],\n  template: \`<hugeicons-icon [icon]="icon" [size]="${size}" color="${color}" [strokeWidth]="${sw}"></hugeicons-icon>\`,\n})\nexport class ExampleComponent {\n  icon = ${exp};\n}`,
        note: `Install from this mirror: ${pkgInstall("@hugeicons/core-free-icons", "@hugeicons/angular")}`,
      };
    case "rn":
      return {
        code: `import { HugeiconsIcon } from "@hugeicons/react-native";\nimport { ${exp} } from "@hugeicons/core-free-icons";\n\n<HugeiconsIcon icon={${exp}} size={${size}} color="${state.color || "#051735"}" strokeWidth={${sw}} />`,
        note: `Needs react-native-svg. Install from this mirror: ${pkgInstall("@hugeicons/core-free-icons", "@hugeicons/react-native")}`,
      };
    case "wc":
      return {
        code: `<script src="${ORIGIN}/hgi.js" defer></script>\n\n<hgi-icon name="${name}"${size !== 24 ? ` size="${size}"` : ""}${state.color ? ` color="${state.color}"` : ""}${state.stroke !== 1.5 ? ` stroke="${sw}"` : ""}></hgi-icon>`,
        note: "A 1 KB custom element that inlines the SVG, so CSS color and currentColor work. No build step.",
      };
    case "url":
      return {
        code: `${ORIGIN}/svg/${name}.svg${qs}\n${ORIGIN}/png/${name}.png?size=${Math.max(size, 64)}${state.color ? `&color=${state.color.replace("#", "")}` : ""}\n\n<img src="${ORIGIN}/svg/${name}.svg${qs}" width="${size}" height="${size}" alt="" />`,
        note: "Query options: size (px), color (hex without #, or a CSS name), stroke (0.25–6). PNG also takes bg.",
      };
    case "esm":
      return {
        code: `import ${exp} from "${ORIGIN}/npm/@hugeicons/core-free-icons@${v}/dist/esm/${exp}.js";`,
        note: "Straight from the browser or Deno, no install. Import the single-icon file, not index.js (that pulls in all 6,100).",
      };
  }
  return { code: "", note: "" };
}

function renderSnippet() {
  if (state.selected < 0) return;
  const { code, note } = snippet(state.selected, state.tab);
  $("#d-code").textContent = code;
  $("#d-note").textContent = note;
  $("#d-tabs").querySelectorAll("button").forEach((b) => b.setAttribute("aria-selected", String(b.dataset.tab === state.tab)));
}

function renderDrawer() {
  const i = state.selected;
  if (i < 0) return;
  const [name, exp, aliases, cp] = state.icons[i]!;
  $("#d-name").textContent = name;
  $("#d-export").textContent = `${exp} · U+${cp.toString(16).toUpperCase()}`;
  $("#d-preview").innerHTML = `<svg viewBox="0 0 24 24" fill="none" role="img" aria-label="${name}">${bodyFor(i)}</svg>`;
  $("#d-sizes").innerHTML = [16, 20, 24, 32, 48]
    .map((s) => `<figure><svg width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" aria-hidden="true">${bodyFor(i)}</svg><figcaption>${s}</figcaption></figure>`)
    .join("");
  $("#d-aliases").innerHTML = aliases
    ? aliases.split(" ").map((a) => `<button class="chip" data-alias="${esc(a)}" title="Search for ${esc(a)}">${esc(a)}</button>`).join("")
    : "";
  const q = new URLSearchParams();
  if (state.color) q.set("color", state.color.replace("#", ""));
  if (state.stroke !== 1.5) q.set("stroke", num(state.stroke));
  const extra = q.toString() ? `&${q}` : "";
  const svgQs = q.toString() ? `?${q}` : "";
  $("#d-downloads").innerHTML = [
    `<a class="btn btn-secondary btn-sm" href="/svg/${name}.svg${svgQs}" download="${name}.svg"><span class="hgi-inline" data-icon="download-04"></span>SVG</a>`,
    `<a class="btn btn-secondary btn-sm" href="/svg-outline/${name}.svg" download="${name}-outline.svg">Outline SVG</a>`,
    ...[64, 128, 256, 512].map((s) => `<a class="btn btn-secondary btn-sm" href="/png/${name}.png?size=${s}${extra}" download="${name}-${s}.png">PNG ${s}</a>`),
    `<button class="btn btn-secondary btn-sm" data-copy-link><span class="hgi-inline" data-icon="link-square-02"></span>Link</button>`,
  ].join("");
  paintUiIcons($("#drawer"));
  renderSnippet();
}

function openIcon(i: number, push = true) {
  const wasOpen = state.selected >= 0 && !$("#drawer").hidden;
  state.selected = i;
  if (!wasOpen) state.lastFocus = document.activeElement as HTMLElement;
  $("#drawer").hidden = false;
  $("#scrim").hidden = false;
  renderDrawer();
  renderVisible(true);
  const name = state.icons[i]![0];
  document.title = `${name} · Evvect Icons`;
  if (push) history[wasOpen ? "replaceState" : "pushState"]({ icon: name }, "", `/icon/${name}${location.search}`);
  if (!wasOpen) $("#d-close").focus();
}

function closeIcon(push = true) {
  if ($("#drawer").hidden) return;
  $("#drawer").hidden = true;
  $("#scrim").hidden = true;
  state.selected = -1;
  renderVisible(true);
  document.title = "Evvect Icons";
  if (push) history.pushState({}, "", `/${location.search}`);
  state.lastFocus?.focus?.();
}

// ---------- docs view ----------

function code(text: string, label?: string) {
  return `${label ? `<p class="code-label">${esc(label)}</p>` : ""}<div class="code"><pre><code>${esc(text)}</code></pre><button class="btn btn-secondary btn-sm" data-copy="${esc(text)}"><span class="hgi-inline" data-icon="copy-01"></span>Copy</button></div>`;
}

function doc(id: string, n: string, title: string, intro: string, body: string) {
  return `<section class="doc card" id="${id}"><div class="doc-head"><span class="doc-num">${n}</span><h2>${title}</h2><p>${intro}</p></div><div class="doc-body">${body}</div></section>`;
}

function renderDocs() {
  const v = state.version;
  const pk = state.packages;
  const rows = Object.entries(pk)
    .map(([name, p]) => `<tr><td>${name}</td><td class="fig">${p.version}</td><td><a href="/npm/${name}@${p.version}/">browse</a> · <a href="/npm/${p.tarball}">tarball</a></td></tr>`)
    .join("");
  const sample = ["home-01", "search-01", "settings-02", "user", "calendar-03", "mail-01", "notification-03", "shopping-cart-01", "delete-02", "favourite"];
  $("#docs").innerHTML = [
    doc("font", "01", "Icon font", "One stylesheet, then an &lt;i&gt; per icon. The class names match the Hugeicons CDN, so an existing site only needs its stylesheet URL changed.",
      code(`<link rel="stylesheet" href="${ORIGIN}/font/hgi-stroke-rounded.css" />`, "In your <head>") +
      code(`<i class="hgi hgi-stroke hgi-home-01"></i>\n<i class="hgi hgi-stroke hgi-search-01 hgi-2x"></i>\n<i class="hgi hgi-stroke hgi-loading-03 hgi-spin"></i>`, "Where you want an icon") +
      `<div class="font-demo">${sample.map((s) => `<i class="hgi hgi-stroke hgi-${s}" title="${s}"></i>`).join("")}<span class="font-demo-label">Rendered with the font</span></div>` +
      `<table class="kv"><thead><tr><th>Class</th><th>Does</th></tr></thead><tbody>
        <tr><td>hgi hgi-stroke</td><td>Base classes (hgi-rounded and hgi-stroke-rounded work too)</td></tr>
        <tr><td>hgi-&lt;name&gt;</td><td>Picks the icon, e.g. hgi-home-01</td></tr>
        <tr><td>hgi-xs … hgi-4x</td><td>0.75em, 0.875em, 1.25em, 1.5em, 2em, 3em, 4em</td></tr>
        <tr><td>hgi-spin</td><td>Rotates (stops for reduced motion)</td></tr>
        <tr><td>hgi-rotate-90/180/270, hgi-flip-h/v</td><td>Transforms</td></tr></tbody></table>` +
      code(`/font/hgi-stroke-rounded.woff2  (also .woff and .ttf)\n/font/codepoints.json\n/font/_hgi-codepoints.scss`, "Also available")),
    doc("svg", "02", "SVG files", "Every icon as its own file. Add query options to recolour or resize it on the server; leave them off for the original.",
      code(`${ORIGIN}/svg/home-01.svg\n${ORIGIN}/svg/home-01.svg?size=32&color=0660d2&stroke=2\n${ORIGIN}/svg-outline/home-01.svg`, "URLs") +
      code(`<img src="${ORIGIN}/svg/home-01.svg" width="24" height="24" alt="Home" />`, "As an image") +
      code(`.icon-home {\n  width: 24px; height: 24px;\n  background: currentColor;\n  mask: url("${ORIGIN}/svg/home-01.svg") center / contain no-repeat;\n}`, "As a CSS mask (follows text colour)") +
      `<table class="kv"><thead><tr><th>Option</th><th>Values</th></tr></thead><tbody>
        <tr><td>size</td><td>Width and height in px, 1–2048 (default 24)</td></tr>
        <tr><td>color</td><td>Hex without # (0660d2), or any CSS colour name (default currentColor)</td></tr>
        <tr><td>stroke</td><td>Stroke width at 24px, 0.25–6 (default 1.5). Thinner and thicker lines scale with it</td></tr></tbody></table>` +
      `<p class="small fg-muted">/svg-outline/ has the same icons with strokes turned into filled shapes: useful for icon fonts, laser cutters, Figma boolean ops, or anywhere strokes don't survive.</p>`),
    doc("png", "03", "PNG", "Rendered on request with resvg and cached. For e-mail, social cards and anything that can't show SVG.",
      code(`${ORIGIN}/png/home-01.png?size=128\n${ORIGIN}/png/home-01.png?size=512&color=ffffff&bg=051735&stroke=2`, "URLs") +
      `<table class="kv"><thead><tr><th>Option</th><th>Values</th></tr></thead><tbody>
        <tr><td>size</td><td>1–2048 px (default 64)</td></tr><tr><td>color</td><td>Default black</td></tr>
        <tr><td>bg</td><td>Background colour (default transparent)</td></tr><tr><td>stroke</td><td>As for SVG</td></tr></tbody></table>`),
    doc("sprite", "04", "SVG sprite", "All 6,100 icons in one file as &lt;symbol&gt;s. Browsers only allow &lt;use&gt; from the same origin, so copy the file to your own site.",
      code(`curl -O ${ORIGIN}/sprite.svg`, "Download") +
      code(`<svg width="24" height="24"><use href="/sprite.svg#hgi-home-01" /></svg>`, "Use (same origin)")),
    doc("web-component", "05", "Web component", "A tiny custom element that fetches and inlines the SVG. Works in plain HTML, WordPress, Webflow and any framework.",
      code(`<script src="${ORIGIN}/hgi.js" defer></script>\n\n<hgi-icon name="home-01"></hgi-icon>\n<hgi-icon name="search-01" size="32" color="#0660d2" stroke="2"></hgi-icon>\n<hgi-icon name="delete-02" label="Delete"></hgi-icon>`) +
      `<table class="kv"><thead><tr><th>Attribute</th><th>Does</th></tr></thead><tbody>
        <tr><td>name</td><td>Icon name (required)</td></tr><tr><td>size</td><td>px, default 1em so it sizes with text</td></tr>
        <tr><td>color</td><td>Default currentColor</td></tr><tr><td>stroke</td><td>Default 1.5</td></tr>
        <tr><td>label</td><td>Accessible name; without it the icon is hidden from screen readers</td></tr></tbody></table>`),
    doc("npm", "06", "npm packages", "The official MIT packages, mirrored byte for byte. Install the tarballs directly, or import single files as ES modules.",
      code(pkgInstall("@hugeicons/core-free-icons", "@hugeicons/react"), "Install (bun, npm, pnpm and yarn all take tarball URLs)") +
      code(`import Home01Icon from "${ORIGIN}/npm/@hugeicons/core-free-icons@${v}/dist/esm/Home01Icon.js";`, "ES module, no install") +
      `<table class="kv"><thead><tr><th>Package</th><th>Version</th><th>Files</th></tr></thead><tbody>${rows}</tbody></table>` +
      `<p class="small fg-muted">/npm/&lt;package&gt;@latest/… redirects to the mirrored version. Directory URLs ending in / return a JSON file list.</p>`),
    doc("api", "07", "JSON API", "Read-only, no key, CORS open. Good for pickers, CMS plugins and scripts.",
      `<table class="kv"><thead><tr><th>Endpoint</th><th>Returns</th></tr></thead><tbody>
        <tr><td>/api/search?q=home&amp;limit=60</td><td>Ranked matches: name, export, aliases, svg URL</td></tr>
        <tr><td>/api/icon/home-01</td><td>One icon: SVG, code point, URLs and usage snippets</td></tr>
        <tr><td>/api/icons.json</td><td>Every icon: [name, export, aliases, codepoint, body]</td></tr>
        <tr><td>/api/names.json</td><td>Every name</td></tr>
        <tr><td>/api/iconify.json</td><td>Iconify icon set (prefix hgi)</td></tr>
        <tr><td>/api/meta.json · /api/packages.json</td><td>Version and mirrored packages</td></tr></tbody></table>` +
      code(`curl "${ORIGIN}/api/search?q=arrow+right&limit=5"`, "Try it")),
    doc("downloads", "08", "Downloads", "Everything in one go, each with the MIT licence included.",
      `<div class="dl-cards">
        <a class="dl-card" href="/download/hugeicons-free-svg.zip" download><strong>All SVGs</strong><span>6,100 files + sprite · ZIP</span></a>
        <a class="dl-card" href="/download/hugeicons-free-svg-outline.zip" download><strong>Outlined SVGs</strong><span>Strokes as fills · ZIP</span></a>
        <a class="dl-card" href="/download/hugeicons-free-font.zip" download><strong>Icon font kit</strong><span>woff2, woff, ttf, CSS, SCSS · ZIP</span></a>
        <a class="dl-card" href="/sprite.svg" download><strong>sprite.svg</strong><span>Every icon as a symbol</span></a>
        <a class="dl-card" href="/api/iconify.json" download><strong>Iconify JSON</strong><span>For Iconify tools</span></a>
        <a class="dl-card" href="/LICENSE.txt"><strong>Licence</strong><span>MIT · Hugeicons</span></a>
      </div>`),
  ].join("");
  paintUiIcons($("#docs"));
  if (!document.querySelector("link[data-hgi-font]")) {
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = "/font/hgi-stroke-rounded.css";
    l.dataset.hgiFont = "";
    document.head.append(l);
  }
}

// ---------- routing ----------

function route(push = false) {
  const path = location.pathname;
  const use = path === "/use" || path.startsWith("/use/");
  $("#view-icons").hidden = use;
  $("#view-use").hidden = !use;
  document.querySelectorAll<HTMLAnchorElement>("[data-nav]").forEach((a) => {
    const n = a.dataset.nav!;
    const on = use ? (n === "use" && !location.hash) || (location.hash === `#${n}`) : n === "icons";
    if (on) a.setAttribute("aria-current", "page");
    else a.removeAttribute("aria-current");
  });
  if (use) {
    closeIcon(false);
    if (!$("#docs").childElementCount) renderDocs();
    if (location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView();
    else if (push) window.scrollTo(0, 0);
    document.title = "Use them · Evvect Icons";
    return;
  }
  layout();
  const m = path.match(/^\/icon\/([^/]+)/);
  if (m) {
    const i = state.byName.get(decodeURIComponent(m[1]!));
    if (i !== undefined) openIcon(i, false);
  } else {
    closeIcon(false);
    document.title = "Evvect Icons";
  }
}

function navigate(href: string) {
  const url = new URL(href, location.href);
  history.pushState({}, "", url);
  route(true);
}

// ---------- theme ----------

const THEMES = ["system", "light", "dark"] as const;
function applyTheme(t: (typeof THEMES)[number]) {
  if (t === "system") delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = t;
  const btn = $("#theme");
  const icon = btn.querySelector<HTMLElement>("[data-icon]")!;
  icon.dataset.icon = t === "light" ? "sun-03" : t === "dark" ? "moon-02" : "computer";
  btn.setAttribute("aria-label", `Theme: ${t}. Switch theme`);
  paintUiIcons(btn);
  store("evvect-icons-theme", t === "system" ? "" : t);
  syncSwatch();
}

/** With no colour picked, the swatch shows the theme's text colour (what the icons use). */
function syncSwatch() {
  if (state.color) return;
  const fg = getComputedStyle(document.documentElement).getPropertyValue("--fg").trim();
  if (/^#[0-9a-f]{6}$/i.test(fg)) $<HTMLInputElement>("#color").value = fg;
}

// ---------- boot ----------

async function boot() {
  grid.el = $("#grid-inner");
  const [data, pkgs] = await Promise.all([
    fetch("/api/icons.json").then((r) => r.json() as Promise<IconsJson>),
    fetch("/api/packages.json").then((r) => r.json()).catch(() => ({})),
  ]);
  state.icons = data.icons;
  state.version = data.version;
  state.packages = pkgs;
  state.icons.forEach((row, i) => {
    state.byName.set(row[0], i);
    state.hay.push({ name: row[0], compact: row[0].replaceAll("-", ""), words: row[0].split("-"), aliases: row[2].toLowerCase() });
  });
  const count = fmt.format(data.count);
  document.querySelectorAll("[data-count]").forEach((el) => (el.textContent = count));
  $("#hero-caps").textContent = `Hugeicons free · Stroke rounded · v${data.version} · MIT`;
  $("#stat-version").textContent = data.version;
  $<HTMLInputElement>("#q").placeholder = `Search ${count} icons`;
  $("#footer-version").textContent = `@hugeicons/core-free-icons ${data.version} · ${count} icons`;
  $("#loading").remove();
  fetch("/font/hgi-stroke-rounded.woff2", { method: "HEAD" })
    .then((r) => {
      const len = Number(r.headers.get("content-length"));
      if (len) $("#stat-font").textContent = `${Math.round(len / 1024)} KB`;
    })
    .catch(() => {});

  paintUiIcons();
  const saved = store("evvect-icons-theme");
  applyTheme(saved === "light" || saved === "dark" ? saved : "system");

  // tabs exist before the first route, which may open the drawer straight away (/icon/<name>)
  $("#d-tabs").innerHTML = TABS.map(([id, label]) => `<button role="tab" data-tab="${id}">${label}</button>`).join("");
  const savedTab = store("evvect-icons-tab");
  if (savedTab && TABS.some(([id]) => id === savedTab)) state.tab = savedTab;

  const q = new URLSearchParams(location.search).get("q") ?? "";
  $<HTMLInputElement>("#q").value = q;
  applySearch(q, false);
  route();
  bind();
}

function focusSearch() {
  if (!$("#view-use").hidden) navigate("/");
  closeIcon();
  const input = $<HTMLInputElement>("#q");
  const toolbar = $(".toolbar");
  if (toolbar.getBoundingClientRect().top > 80 || window.scrollY < 10) {
    const y = $("#grid").getBoundingClientRect().top + window.scrollY - 180;
    window.scrollTo({ top: Math.max(0, y), behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  }
  input.focus();
  input.select();
}

function bind() {
  const input = $<HTMLInputElement>("#q");
  input.addEventListener("input", () => {
    clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => {
      applySearch(input.value);
      const g = $("#grid").getBoundingClientRect().top;
      if (g < 0) window.scrollTo({ top: window.scrollY + g - 160 });
    }, 60);
  });
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && state.results.length) openIcon(state.results[0]!);
    if (e.key === "Escape") {
      input.value = "";
      applySearch("");
    }
  });

  $("#size").addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-v]");
    if (!b) return;
    state.size = Number(b.dataset.v);
    $("#size").querySelectorAll("button").forEach((x) => x.setAttribute("aria-checked", String(x === b)));
    document.documentElement.style.setProperty("--icon-size", `${state.size}px`);
    renderSnippet();
  });
  const stroke = $<HTMLInputElement>("#stroke");
  stroke.addEventListener("input", () => {
    state.stroke = Number(stroke.value);
    $("#stroke-v").textContent = num(state.stroke);
    renderVisible(true);
    renderDrawer();
  });
  const color = $<HTMLInputElement>("#color");
  color.addEventListener("input", () => {
    state.color = color.value;
    document.documentElement.style.setProperty("--icon-color", color.value);
    renderSnippet();
    renderDrawer();
  });
  $("#color-reset").addEventListener("click", () => {
    state.color = "";
    document.documentElement.style.removeProperty("--icon-color");
    syncSwatch();
    renderVisible(true);
    renderDrawer();
  });

  grid.el.addEventListener("click", (e) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>(".tile");
    if (t) openIcon(Number(t.dataset.i));
  });
  window.addEventListener("scroll", () => {
    renderVisible();
    $(".top").classList.toggle("scrolled", window.scrollY > 8);
  }, { passive: true });
  let resizeRaf = 0;
  window.addEventListener("resize", () => {
    cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(layout);
  });

  $("#d-tabs").addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-tab]");
    if (!b) return;
    state.tab = b.dataset.tab!;
    store("evvect-icons-tab", state.tab);
    renderSnippet();
  });
  $("#d-copy").addEventListener("click", () => copy($("#d-code").textContent ?? "", "Copied to clipboard"));
  $("#d-close").addEventListener("click", () => closeIcon());
  $("#scrim").addEventListener("click", () => closeIcon());
  $("#d-aliases").addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-alias]");
    if (!b) return;
    closeIcon();
    input.value = b.dataset.alias!;
    applySearch(input.value);
  });
  $("#d-downloads").addEventListener("click", (e) => {
    if ((e.target as HTMLElement).closest("[data-copy-link]") && state.selected >= 0) {
      copy(`${ORIGIN}/icon/${state.icons[state.selected]![0]}`, "Link copied");
    }
  });
  $("#docs").addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-copy]");
    if (b) copy(b.dataset.copy!, "Copied to clipboard");
  });

  $("#open-search").addEventListener("click", focusSearch);
  $("#theme").addEventListener("click", () => {
    const cur = (document.documentElement.dataset.theme as "light" | "dark" | undefined) ?? "system";
    applyTheme(THEMES[(THEMES.indexOf(cur) + 1) % THEMES.length]!);
  });

  document.addEventListener("click", (e) => {
    const a = (e.target as HTMLElement).closest<HTMLAnchorElement>("a[href]");
    if (!a || a.target || a.hasAttribute("download") || e.metaKey || e.ctrlKey || e.shiftKey) return;
    const url = new URL(a.href);
    if (url.origin !== location.origin) return;
    const p = url.pathname;
    const spa = p === "/" || p === "/use" || p.startsWith("/icon/");
    if (!spa) return;
    if (p === location.pathname && url.hash && url.search === location.search) return; // in-page anchor
    e.preventDefault();
    navigate(url.href);
  });
  window.addEventListener("popstate", () => route());
  matchMedia("(prefers-color-scheme: dark)").addEventListener("change", syncSwatch);

  document.addEventListener("keydown", (e) => {
    const typing = (e.target as HTMLElement).matches("input, textarea, [contenteditable]");
    if ((e.key === "k" && (e.ctrlKey || e.metaKey)) || (e.key === "/" && !typing)) {
      e.preventDefault();
      focusSearch();
    } else if (e.key === "Escape" && !$("#drawer").hidden) {
      closeIcon();
    } else if (!$("#drawer").hidden && !typing && (e.key === "ArrowRight" || e.key === "ArrowLeft")) {
      // step through the current results without closing the drawer
      const k = state.results.indexOf(state.selected);
      const next = state.results[k + (e.key === "ArrowRight" ? 1 : -1)];
      if (next !== undefined) {
        e.preventDefault();
        openIcon(next);
      }
    } else if (e.key === "Tab" && !$("#drawer").hidden) {
      // keep focus inside the dialog
      const f = [...$("#drawer").querySelectorAll<HTMLElement>("button, a[href], [tabindex]:not([tabindex='-1'])")];
      if (!f.length) return;
      const first = f[0]!, last = f[f.length - 1]!;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });
}

boot().catch((err) => {
  console.error(err);
  const l = document.getElementById("loading");
  if (l) l.textContent = "Couldn't load the icons. Refresh to try again.";
});
